import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faEdit } from '@fortawesome/free-solid-svg-icons';
import {
  ClassificationGroup,
  Client,
  UploadType,
  Address,
  ClientStatus,
  ClientInput,
  ChargeType,
} from '@core/generated/gql-types';
import { getStatusStyles } from '@/utils/styles.util';
import { useCoreLoopConfigurationStore } from '@core/coreLoopConfiguration/useCoreLoopConfigurationStore';
import { useClassificationStore } from '@core/classification/useClassificationStore';
import {
  useTemplateStore,
  TemplateTerm,
} from '@core/template/useTemplateStore';
import {
  clientChargeTypeOptions,
  formatChargeTypeLabel,
  getClientFields,
  getContactFields,
  isMarginChargeType,
} from '@/utils/client.constants';
import { getCustomAttribute } from '@/utils/utils';
import * as Yup from 'yup';
import FileUpload from '../shared/FileUpload';
import { useClientStore } from '@core/client/useClientStore';
import AddressForm from '../forms/AddressForm';
import SingleSelect from '../shared/SingleSelect';
import MultiSelect from '../shared/MultiSelect';
import Button from '../Button';

type ClientDetailsFormValues = Omit<ClientInput, 'classifications'> & {
  classifications: ClassificationGroup[];
  abn: string;
  acn: string;
  status: ClientStatus;
  chargeType: ChargeType;
  email: string;
  phoneNumber1: string;
  phoneNumber2: string;
  website: string;
};

interface ClientDetailsCardProps {
  client: Client;
  onUpdateClient?: (
    updatedClient: Partial<Client>,
  ) => Promise<{ success: boolean; error?: string } | void>;
  onLogoUpdate?: (newLogoUrl: string) => void;
}

const buildClientDetailsValidationSchema = (
  marginOverridePercentageEnabled: boolean | undefined,
) =>
  Yup.object().shape({
    name: Yup.string().required('Company name is required'),
    classifications: Yup.array().min(1, 'At least one industry is required'),
    address: Yup.object().shape({
      addressLine1: Yup.string().required('Address is required'),
      addressLine2: Yup.string().nullable().notRequired(),
      suburb: Yup.string().nullable().notRequired(),
      state: Yup.string().nullable().notRequired(),
      postcode: Yup.string().nullable().notRequired(),
      countryLabel: Yup.string().nullable().notRequired(),
      countryValue: Yup.string().nullable().notRequired(),
    }),
    abn: Yup.string()
      .test('abn', 'ABN must be 11 digits', (value) => {
        if (!value) return false;
        value = value.replaceAll(' ', '');
        return /^\d{11}$/.test(value);
      })
      .required('An Australian Business Number is required'),
    marginOverridePercentage: marginOverridePercentageEnabled
      ? Yup.number()
          .min(0, 'Margin override percentage must be between 0 and 100')
          .max(100, 'Margin override percentage must be between 0 and 100')
          .when('chargeType', {
            is: ChargeType.Margin,
            then: (schema) =>
              schema.required('Margin override percentage is required'),
            otherwise: (schema) => schema.notRequired(),
          })
      : Yup.number()
          .min(0, 'Margin override percentage must be between 0 and 100')
          .max(100, 'Margin override percentage must be between 0 and 100')
          .nullable()
          .notRequired(),
    paymentTerm: Yup.number()
      .min(1, 'Payment term must be at least 1 day')
      .max(30, 'Payment term must be 30 days or less'),
    acn: Yup.string().test('acn', 'ACN must be 9 digits', (value) => {
      if (!value) return true;
      const cleaned = value.replaceAll(' ', '');
      return /^\d{9}$/.test(cleaned);
    }),
    status: Yup.string().required('Status is required'),
    chargeType: Yup.mixed<ChargeType>()
      .oneOf(
        [ChargeType.Fixed, ChargeType.Margin, ChargeType.RateCard],
        'Invalid charge type',
      )
      .required('Charge type is required'),
  });

const ClientContactValidationSchema = Yup.object().shape({
  email: Yup.string()
    .transform((value) => (value?.trim() ? value.trim() : undefined))
    .email('Invalid email')
    .optional(),
  phoneNumber1: Yup.string(),
  phoneNumber2: Yup.string(),
  website: Yup.string(),
});

const flattenFormikErrors = (errors: Record<string, unknown>): string[] => {
  return Object.values(errors).flatMap((value) => {
    if (typeof value === 'string') {
      return [value];
    }
    if (value && typeof value === 'object') {
      return flattenFormikErrors(value as Record<string, unknown>);
    }
    return [];
  });
};

const serializeAddress = (address?: Address | null): string =>
  [
    address?.addressLine1,
    address?.addressLine2,
    address?.suburb,
    address?.state,
    address?.postcode,
    address?.countryLabel,
  ].join('|');

/** GraphQL optional strings may be null; normalise before validation/submit. */
const normalizeAddressField = (value?: string | null): string => value ?? '';

const normalizeAddressForInput = (
  address?: Address | null,
): Address | undefined => {
  if (!address?.addressLine1?.trim()) {
    return undefined;
  }

  return {
    addressLine1: address.addressLine1,
    addressLine2: normalizeAddressField(address.addressLine2),
    suburb: normalizeAddressField(address.suburb),
    state: normalizeAddressField(address.state),
    postcode: normalizeAddressField(address.postcode),
    countryLabel: normalizeAddressField(address.countryLabel),
    countryValue: normalizeAddressField(address.countryValue),
    addressFriendly: address.addressFriendly ?? undefined,
  };
};

const ClientDetailsCard: React.FC<ClientDetailsCardProps> = ({
  client,
  onUpdateClient,
  onLogoUpdate,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editingSection, setEditingSection] = useState<
    'details' | 'contact' | null
  >(null);

  const { setClientStatus } = useClientStore();

  const {
    classificationGroups,
    fetchClassificationGroups,
    getClassificationGroupsByClassifications,
    isLoading: isLoadingClassifications,
  } = useClassificationStore();

  const { fetchAndSetCoreLoopConfiguration } = useCoreLoopConfigurationStore();

  const { coreLoopConfiguration } = useCoreLoopConfigurationStore();

  const { getTerm } = useTemplateStore();

  useEffect(() => {
    if (client.tenantId) {
      fetchAndSetCoreLoopConfiguration(client.tenantId);
      fetchClassificationGroups(client.tenantId);
    }
  }, [
    client.tenantId,
    fetchAndSetCoreLoopConfiguration,
    fetchClassificationGroups,
  ]);

  const marginOverridePercentageEnabled =
    coreLoopConfiguration?.configuration?.allowClientMarginOverride;

  const coreLoopMarginPercentage = useMemo(() => {
    return coreLoopConfiguration?.configuration?.marginPercentage
      ? Math.round(coreLoopConfiguration?.configuration?.marginPercentage * 100)
      : undefined;
  }, [coreLoopConfiguration?.configuration]);

  const initialClassifications = useMemo<ClassificationGroup[]>(() => {
    return getClassificationGroupsByClassifications(
      client.classifications ?? [],
    );
  }, [client.classifications, getClassificationGroupsByClassifications]);

  const clientDetailsValidationSchema = useMemo(
    () => buildClientDetailsValidationSchema(marginOverridePercentageEnabled),
    [marginOverridePercentageEnabled],
  );

  // Custom attributes
  const clientFields = getClientFields(client, classificationGroups);
  const contactFields = getContactFields(client);

  const handleEdit = (section: 'details' | 'contact') => {
    setEditingSection(section);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingSection(null);
  };

  const handleAddressChange = useCallback(
    (
      address: Address | null,
      setFieldValue: (field: string, value: unknown) => void,
      currentAddress?: Address | null,
    ) => {
      if (!address?.addressLine1?.trim()) {
        return;
      }
      if (
        currentAddress?.addressLine1 &&
        serializeAddress(address) === serializeAddress(currentAddress)
      ) {
        return;
      }
      setFieldValue('address', normalizeAddressForInput(address));
    },
    [],
  );

  const handleSubmit = async (
    values: ClientDetailsFormValues,
    {
      setStatus,
    }: {
      setStatus: (status?: string) => void;
    },
  ) => {
    setStatus(undefined);

    try {
      if (!onUpdateClient) {
        return;
      }

      const selectedClassificationIds = values.classifications.flatMap(
        (group) =>
          group.classifications
            ?.map((classification) => classification?.id)
            .filter(Boolean) ?? [],
      ) as string[];

      // Format data according to updateClient API structure
      const updatedClient: ClientInput = {
        name: values.name,
        address: normalizeAddressForInput(values.address),
        marginOverridePercentage:
          values.marginOverridePercentage != null
            ? values.marginOverridePercentage / 100
            : values.marginOverridePercentage,
        paymentTerm: values.paymentTerm,
        chargeType: values.chargeType,
        customAttributes: [
          { ref: 'abn', value: values.abn.replaceAll(' ', '') },
          { ref: 'acn', value: values.acn.replaceAll(' ', '') },
          { ref: 'email', value: values.email },
          { ref: 'phoneNumber1', value: values.phoneNumber1 },
          { ref: 'phoneNumber2', value: values.phoneNumber2 },
          { ref: 'website', value: values.website },
        ].filter((attr) => attr.value !== undefined && attr.value !== ''),
      };

      if (editingSection === 'details') {
        updatedClient.classifications = selectedClassificationIds;
      }

      const result = await onUpdateClient(
        updatedClient as unknown as Partial<Client>,
      );

      if (result && !result.success) {
        setStatus(result.error ?? 'Failed to update client');
        return;
      }

      // Update client status separately if it has changed
      if (editingSection === 'details' && values.status !== client.status) {
        const statusResult = await setClientStatus(client.id, values.status);
        if (!statusResult.success) {
          setStatus(statusResult.error ?? 'Failed to update client status');
          return;
        }
      }

      setIsEditing(false);
      setEditingSection(null);
    } catch (error) {
      console.error('Error updating client:', error);
      setStatus(
        error instanceof Error ? error.message : 'Failed to update client',
      );
    }
  };

  const initialClassificationIds = useMemo(
    () =>
      initialClassifications
        .map((group) => group.id)
        .filter(Boolean)
        .sort()
        .join(','),
    [initialClassifications],
  );

  const initialValues = useMemo<ClientDetailsFormValues>(
    () => ({
      name: client.name || '',
      classifications: initialClassifications,
      address: normalizeAddressForInput(client?.address) || {
        addressLine1: '',
        addressLine2: '',
        suburb: '',
        state: '',
        postcode: '',
        countryLabel: '',
        countryValue: '',
      },
      abn: getCustomAttribute(client, 'abn') || '',
      marginOverridePercentage:
        client.marginOverridePercentage != null
          ? Math.round(client.marginOverridePercentage * 100)
          : coreLoopMarginPercentage,
      paymentTerm: client.paymentTerm || 7,
      acn: getCustomAttribute(client, 'acn') || '',
      status: (client.status as ClientStatus) || ClientStatus.Pending,
      chargeType: client.chargeType || ChargeType.Margin,
      email: getCustomAttribute(client, 'email') || '',
      phoneNumber1: getCustomAttribute(client, 'phoneNumber1') || '',
      phoneNumber2: getCustomAttribute(client, 'phoneNumber2') || '',
      website: getCustomAttribute(client, 'website') || '',
    }),
    [client, initialClassifications, coreLoopMarginPercentage],
  );

  return (
    <div className="flex w-full gap-6">
      {/* Left Column */}
      <div className="flex w-1/2 flex-col gap-6">
        {/* Client Logo Card */}
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-3">
            <span className="text-base font-semibold text-gray-800">
              {getTerm(TemplateTerm.CLIENT, false)} Logo
            </span>
            <button className="flex cursor-pointer items-center gap-2 text-sm font-medium text-blue-600 hover:underline">
              <FileUpload
                onUploadComplete={onLogoUpdate || (() => {})}
                uploadType={UploadType.ClientLogo}
                className="h-full w-full"
                previewClassName="w-16 h-16 rounded-full overflow-hidden"
                objectId={client.id}
              >
                <FontAwesomeIcon icon={faUpload} className="h-4 w-4" />
                Upload
              </FileUpload>
            </button>
          </div>
          <div className="p-6">
            <div className="flex w-full justify-center">
              <div
                className="flex items-center justify-center rounded-md bg-gray-100"
                style={{ width: 180, height: 80 }}
              >
                {client.logoUrl ? (
                  <img
                    src={client.logoUrl}
                    alt="Company Logo"
                    className="max-h-16 max-w-[120px] object-contain"
                  />
                ) : (
                  <div className="h-12 w-24 rounded-sm bg-gray-200" />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Contact Details Card */}
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-gray-100 px-6 py-3">
            <span className="text-base font-semibold text-gray-800">
              Contact Details
            </span>
            <button
              onClick={() => handleEdit('contact')}
              className="flex cursor-pointer items-center gap-2 text-sm font-medium text-blue-600 hover:underline"
            >
              <FontAwesomeIcon icon={faEdit} className="h-4 w-4" />
              Edit
            </button>
          </div>
          <div className="p-6">
            {editingSection === 'contact' && isEditing ? (
              <Formik
                initialValues={initialValues}
                validationSchema={ClientContactValidationSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting, status }) => (
                  <Form className="space-y-4">
                    {status && (
                      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                        {status}
                      </div>
                    )}
                    <div className="space-y-4">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Email
                        </label>
                        <Field
                          name="email"
                          type="email"
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                        <ErrorMessage
                          name="email"
                          component="div"
                          className="mt-1 text-xs text-red-500"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Phone Number 1
                        </label>
                        <Field
                          name="phoneNumber1"
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                        <ErrorMessage
                          name="phoneNumber1"
                          component="div"
                          className="mt-1 text-xs text-red-500"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Phone Number 2
                        </label>
                        <Field
                          name="phoneNumber2"
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                        <ErrorMessage
                          name="phoneNumber2"
                          component="div"
                          className="mt-1 text-xs text-red-500"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Website
                        </label>
                        <Field
                          name="website"
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                        <ErrorMessage
                          name="website"
                          component="div"
                          className="mt-1 text-xs text-red-500"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancel}
                        className="px-4 py-2 text-sm font-medium"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={isSubmitting}
                        className="px-4 py-2 text-sm font-medium"
                      >
                        Done
                      </Button>
                    </div>
                  </Form>
                )}
              </Formik>
            ) : (
              <dl className="space-y-4">
                {contactFields.map(({ label, value }) => (
                  <div
                    className="flex items-center justify-between"
                    key={label}
                  >
                    <dt className="text-sm text-gray-500">{label}</dt>
                    <dd className="text-sm font-medium text-blue-600">
                      {value}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        </div>
      </div>

      {/* Right: Client Details Card */}
      <div className="w-1/2 rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-3">
          <span className="text-base font-semibold text-gray-800">
            {getTerm(TemplateTerm.CLIENT, false)} Details
          </span>
          <button
            onClick={() => handleEdit('details')}
            className="flex cursor-pointer items-center gap-2 text-sm font-medium text-blue-600 hover:underline"
          >
            <FontAwesomeIcon icon={faEdit} className="h-4 w-4" />
            Edit
          </button>
        </div>
        <div className="p-6">
          {editingSection === 'details' && isEditing ? (
            <Formik
              key={`client-details-${client.id}-${initialClassificationIds}`}
              initialValues={initialValues}
              validationSchema={clientDetailsValidationSchema}
              onSubmit={handleSubmit}
            >
              {({
                isSubmitting,
                setFieldValue,
                setFieldTouched,
                values,
                touched,
                errors,
                status,
                submitCount,
              }) => {
                const validationMessages = flattenFormikErrors(
                  errors as Record<string, unknown>,
                );
                const showValidationSummary =
                  submitCount > 0 && validationMessages.length > 0 && !status;

                return (
                  <Form className="space-y-4">
                    {(status || showValidationSummary) && (
                      <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600">
                        {status ?? validationMessages.join('. ')}
                      </div>
                    )}
                    <div className="space-y-4">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Company Name
                        </label>
                        <Field
                          name="name"
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                        <ErrorMessage
                          name="name"
                          component="div"
                          className="mt-1 text-xs text-red-500"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Industries
                        </label>
                        <MultiSelect
                          options={classificationGroups}
                          value={values.classifications}
                          onChange={(value) => {
                            setFieldValue('classifications', value);
                            setFieldTouched('classifications', true, false);
                          }}
                          placeholder="Select industries"
                          disabled={isLoadingClassifications}
                          isLoading={isLoadingClassifications}
                          loadingText="Loading industries..."
                          emptyText="No industries available"
                          error={
                            !!(
                              touched.classifications && errors.classifications
                            )
                          }
                        />
                        <ErrorMessage
                          name="classifications"
                          component="div"
                          className="mt-1 text-xs text-red-500"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Address
                        </label>
                        <Field name="address">
                          {({
                            form,
                          }: {
                            form: {
                              setFieldValue: (
                                field: string,
                                value: unknown,
                              ) => void;
                              values: {
                                address?: Address | null;
                              };
                            };
                          }) => (
                            <AddressForm
                              key={serializeAddress(client?.address)}
                              initialAddress={client?.address ?? undefined}
                              onAddressChange={(address) =>
                                handleAddressChange(
                                  address,
                                  form.setFieldValue,
                                  form.values.address,
                                )
                              }
                            />
                          )}
                        </Field>
                        <ErrorMessage
                          name="address.addressLine1"
                          component="div"
                          className="mt-1 text-xs text-red-500"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          ABN
                        </label>
                        <Field
                          name="abn"
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                        <ErrorMessage
                          name="abn"
                          component="div"
                          className="mt-1 text-xs text-red-500"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          ACN
                        </label>
                        <Field
                          name="acn"
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        />
                        <ErrorMessage
                          name="acn"
                          component="div"
                          className="mt-1 text-xs text-red-500"
                        />
                      </div>
                      {marginOverridePercentageEnabled &&
                        isMarginChargeType(values.chargeType) && (
                          <div>
                            <label className="mb-1 block text-sm font-medium text-gray-700">
                              Margin Override Percentage (%)
                            </label>
                            <p className="mb-1 text-xs text-gray-500">
                              Enter a whole number between 0 and 100.
                            </p>
                            <input
                              type="number"
                              inputMode="numeric"
                              min={0}
                              max={100}
                              step={1}
                              id="marginOverridePercentage"
                              name="marginOverridePercentage"
                              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                              value={values.marginOverridePercentage ?? ''}
                              onChange={(e) => {
                                const rawValue = e.target.value;
                                setFieldValue(
                                  'marginOverridePercentage',
                                  rawValue === ''
                                    ? undefined
                                    : Number(rawValue),
                                );
                              }}
                              onKeyDown={(e) => {
                                const allowedControlKeys = [
                                  'Backspace',
                                  'Delete',
                                  'ArrowLeft',
                                  'ArrowRight',
                                  'ArrowUp',
                                  'ArrowDown',
                                  'Tab',
                                  'Home',
                                  'End',
                                ];
                                if (
                                  !/^\d$/.test(e.key) &&
                                  !allowedControlKeys.includes(e.key)
                                ) {
                                  e.preventDefault();
                                }
                              }}
                            />
                            <ErrorMessage
                              name="marginOverridePercentage"
                              component="div"
                              className="mt-1 text-xs text-red-500"
                            />
                          </div>
                        )}
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Payment Term
                        </label>

                        <SingleSelect
                          options={[7, 14, 30]}
                          value={values.paymentTerm ?? 7}
                          onChange={(value) =>
                            setFieldValue('paymentTerm', value)
                          }
                          optionLabel={(option) => `${option} days`}
                        />

                        <ErrorMessage
                          name="paymentTerm"
                          component="div"
                          className="mt-1 text-xs text-red-500"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Charge Type
                        </label>
                        <SingleSelect<ChargeType>
                          options={clientChargeTypeOptions}
                          value={values.chargeType || ChargeType.Margin}
                          onChange={(value) => {
                            setFieldValue('chargeType', value);
                          }}
                          optionLabel={(option) =>
                            formatChargeTypeLabel(option)
                          }
                        />
                        <ErrorMessage
                          name="chargeType"
                          component="div"
                          className="mt-1 text-xs text-red-500"
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700">
                          Status
                        </label>
                        <SingleSelect
                          options={[ClientStatus.Active, ClientStatus.Pending]}
                          value={values.status || ''}
                          onChange={(value) => {
                            setFieldValue('status', value);
                          }}
                        />
                        <ErrorMessage
                          name="status"
                          component="div"
                          className="mt-1 text-xs text-red-500"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCancel}
                        className="px-4 py-2 text-sm font-medium"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={isSubmitting}
                        className="px-4 py-2 text-sm font-medium"
                      >
                        Done
                      </Button>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          ) : (
            <dl className="space-y-4">
              {clientFields.map(({ label, value }) => (
                <div className="flex items-start justify-between" key={label}>
                  <dt className="text-sm text-gray-500">{label}</dt>
                  <dd
                    className={`text-right text-sm font-medium text-gray-900 ${label === 'Status' ? 'w-20' : ''}`}
                  >
                    {label === 'Status' ? (
                      <span
                        className={`inline-block rounded-full px-2 py-1 text-xs font-medium ${getStatusStyles(value as ClientStatus)}`}
                      >
                        {value}
                      </span>
                    ) : (
                      value
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientDetailsCard;
